/**
 * padaColours.ts — canonical colour constants for the CAD grid overlay.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  LOCKED 2026-06-07 (memory `reference_grid_cad_overlay_spec`, after ~8
 *  owner preview rounds). The visual reference is the v7 preview HTML
 *  (`Owner's Inbox/04_Projects/AVBP/CAD_GEOMETRY_OptionC_v7_2026-06-07.html`);
 *  the colour arrays here are PORTED VERBATIM from that preview's `ZCOL`,
 *  `CMAP`, `PNAME` and `PADA` constants. The on-screen tracer overlay must
 *  match the preview pixel-for-pixel, so these are owner-dictated constants,
 *  NOT derived at runtime.
 *
 *  ZONE COLOURS — canonical Panchatattva (per
 *  `05_PKA_Reference/AV_VASTU_EFFECTS_DB/zone_prakriti_map_16zone.json`) with
 *  ONE owner override applied HERE only: NW = White/grey `#9aa1aa` (the
 *  canonical DB has NW = Earth/Yellow). This override is COLOUR-ONLY for the
 *  overlay; the DB's element/metal (which drive bar-chart grouping + remedies)
 *  are deliberately NOT touched — the canonical NW element reconciliation is a
 *  flagged owner question (colour-only White, or full Space?). See the v7
 *  preview `ZCOL` for the locked sequence (note NNW = Blue, WSW/W/WNW/NW all
 *  map to the White/grey hex).
 *
 *  PADA COLOURS — the owner-dictated 32-value `entrColour` set (verbatim in
 *  `reference_grid_cad_overlay_spec` + the preview `PNAME`). Inner-ring label
 *  text is drawn in each pada's colour.
 *
 *  PURE — no DOM, no Firebase. Unit-tested (`test/padaColours.test.cjs`):
 *  asserts 32/32 pada coverage + every value resolves to a known hex.
 * ─────────────────────────────────────────────────────────────────────────
 */

/** The five locked overlay hexes (memory `reference_grid_cad_overlay_spec`). */
export const OVERLAY_HEX = {
  blue: '#1D4ED8',
  green: '#15803D',
  red: '#B91C1C',
  yellow: '#CA8A04',
  white: '#9aa1aa', // White/grey
} as const;
export type OverlayColourName = keyof typeof OVERLAY_HEX;

/** Named-colour → hex (preview `CMAP`). */
export const CMAP: Record<OverlayColourName, string> = {
  white: OVERLAY_HEX.white,
  green: OVERLAY_HEX.green,
  blue: OVERLAY_HEX.blue,
  red: OVERLAY_HEX.red,
  yellow: OVERLAY_HEX.yellow,
};

/**
 * 16 ZONE colours, index 0..15 matching ZONE_LABELS
 * ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'].
 * VERBATIM from the preview `ZCOL` — canonical Panchatattva + NW=White override.
 *   N/NNE/NE = Blue (Water)        ENE/E/ESE = Green (Air)
 *   SE/SSE/S = Red (Fire)          SSW/SW = Yellow (Earth)
 *   WSW/W/WNW = White (Space)      NW = White (owner override; DB=Earth/Yellow)
 *   NNW = Blue (Water)
 */
export const ZONE_OVERLAY_COLOURS: string[] = [
  OVERLAY_HEX.blue, // 0  N
  OVERLAY_HEX.blue, // 1  NNE
  OVERLAY_HEX.blue, // 2  NE
  OVERLAY_HEX.green, // 3  ENE
  OVERLAY_HEX.green, // 4  E
  OVERLAY_HEX.green, // 5  ESE
  OVERLAY_HEX.red, // 6  SE
  OVERLAY_HEX.red, // 7  SSE
  OVERLAY_HEX.red, // 8  S
  OVERLAY_HEX.yellow, // 9  SSW
  OVERLAY_HEX.yellow, // 10 SW
  OVERLAY_HEX.white, // 11 WSW
  OVERLAY_HEX.white, // 12 W
  OVERLAY_HEX.white, // 13 WNW
  OVERLAY_HEX.white, // 14 NW  (owner override → White)
  OVERLAY_HEX.blue, // 15 NNW
];

/**
 * Pada label → owner-dictated colour NAME (preview `PNAME`). 32 entries
 * covering N1–N8, E1–E8, S1–S8, W1–W8.
 */
export const PADA_COLOUR_NAME: Record<string, OverlayColourName> = {
  N1: 'white', N2: 'white', N3: 'white', N4: 'white',
  N5: 'green', N6: 'white', N7: 'blue', N8: 'blue',
  E1: 'green', E2: 'blue', E3: 'blue', E4: 'red',
  E5: 'green', E6: 'yellow', E7: 'red', E8: 'red',
  S1: 'red', S2: 'green', S3: 'yellow', S4: 'yellow',
  S5: 'yellow', S6: 'white', S7: 'red', S8: 'red',
  W1: 'white', W2: 'white', W3: 'yellow', W4: 'white',
  W5: 'yellow', W6: 'yellow', W7: 'white', W8: 'blue',
};

/** The 32 pada labels in ring order (excludes N1–N4 wrap? — full set). */
export const ALL_PADA_LABELS: string[] = Object.keys(PADA_COLOUR_NAME);

/** Resolve a pada label (e.g. 'N5') to its overlay hex; '' on unknown. */
export function padaColour(label: string): string {
  const name = PADA_COLOUR_NAME[label];
  return name ? CMAP[name] : '';
}

/** Resolve a zone index (0..15) to its overlay hex. */
export function zoneColour(zoneIndex: number): string {
  return ZONE_OVERLAY_COLOURS[((zoneIndex % 16) + 16) % 16] || '';
}
